package com.sddamico.draftmachine.models

import com.sddamico.draftmachine.data.DraftOrder
import com.sddamico.draftmachine.data.DraftStatus
import com.sddamico.draftmachine.data.PickOrigin
import kotlinx.serialization.Serializable

/** Frontend mirror of the jvmMain `models/DraftModels.kt` `DraftResponse` (timestamps are ISO-8601). */
@Serializable
data class DraftResponse(
  val id: String,
  val playerPoolId: String? = null,  // null = free-text draft (no pool attached)
  val rounds: Int,
  val status: DraftStatus,
  val draftOrder: DraftOrder? = null,
  val createdAt: String? = null,
  val startedAt: String? = null,
  val completedAt: String? = null,
  val pickTimerMinutes: Int? = null,
  val quietHours: QuietHoursSettings = QuietHoursSettings(),
)

/**
 * Frontend mirror of the jvmMain `QuietHoursSettings` — the draft's overnight pause for the pick
 * timer. [startMinute]/[endMinute] are minutes from local midnight in [zone] (an IANA id); start >
 * end wraps midnight.
 */
@Serializable
data class QuietHoursSettings(
  val enabled: Boolean = true,
  val startMinute: Int = 0,     // 12:00 AM
  val endMinute: Int = 360,     // 6:00 AM
  val zone: String = "America/New_York",
)

/** Frontend mirror of the jvmMain `CreateDraftRequest`. */
@Serializable
data class CreateDraftRequest(
  val leagueId: String,
  val playerPoolId: String? = null,
  val rounds: Int,
  val draftOrder: DraftOrder? = null,
)

/** Frontend mirror of the jvmMain `AddTeamToDraftRequest`: create a team and attach it to the draft. */
@Serializable
data class AddTeamToDraftRequest(
  val draftId: String,
  val teamName: String? = null,
  val ownerName: String? = null,
  val ownerEmail: String? = null,
  val externalTeamId: String? = null,
)

/** Frontend mirror of the jvmMain `UpdateDraftTeamRequest`. */
@Serializable
data class UpdateDraftTeamRequest(
  val draftId: String,
  val teamId: String,
  val teamName: String,
  val ownerName: String,
  val ownerEmail: String,
)

/**
 * One pick in an app-supplied draft order. [round] is the exact round (comp picks make it diverge
 * from `position / teamCount`); [originTeamId] + [origin] stamp traded/compensatory picks.
 * Frontend mirror of the (new) jvmMain `OrderEntry`.
 */
@Serializable
data class OrderEntry(
  val teamId: String,
  val round: Int? = null,
  val originTeamId: String? = null,
  val origin: PickOrigin? = null,
  val skipped: Boolean = false,
)

/**
 * Frontend mirror of the (extended) jvmMain `SetDraftOrderRequest`. The wizard always sends
 * [entries] (with metadata); the legacy [teamIds] path is left for other callers.
 */
@Serializable
data class SetDraftOrderRequest(
  val draftId: String,
  val teamIds: List<String> = emptyList(),
  val entries: List<OrderEntry>? = null,
)

/** Frontend mirror of the jvmMain `StartDraftRequest`. */
@Serializable
data class StartDraftRequest(
  val draftId: String,
)

/** Frontend mirror of the jvmMain `CancelDraftRequest`. */
@Serializable
data class CancelDraftRequest(
  val draftId: String,
)

/**
 * Frontend mirror of the (extended) jvmMain `UpdateDraftSettingsRequest`. [pickTimerMinutes] is
 * always applied (null = no timer); [quietHours] is left unchanged when omitted, so the timer and
 * the overnight pause save independently.
 */
@Serializable
data class UpdateDraftSettingsRequest(
  val draftId: String,
  val pickTimerMinutes: Int? = null,
  val quietHours: QuietHoursSettings? = null,
)

@Serializable
data class UpdateDraftPickNotificationsRequest(
  val draftId: String,
  val enabled: Boolean,
)

@Serializable
data class DraftPickNotificationsResponse(
  val enabled: Boolean,
)

@Serializable
data class SetDraftAdminsRequest(
  val draftId: String,
  val teamIds: List<String>,
)
